import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { isDevEnvironment, BASE_APP_TITLE, DEV_TITLE_SUFFIX } from '../utils/environment';
import { CAP, can, roleBadgeClass, roleLabel } from '../utils/permissions';

function Header({ authUser, onLogout, language, onLanguageChange, t, hasUnseenOrders, onSummonTour }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Was `authUser?.email?.endsWith('@xebia.com')`, which disagreed with the
  // route guard: @gcompany.nl users passed <ProtectedRoute> but got no menu, so
  // they reached the admin screens by typing URLs instead. Both now ask the same
  // question, and the answer comes from the server-side capability list.
  const canSeeAdminMenu = can(authUser, CAP.VIEW_ADMIN_SCREENS);
  const canManageUsers = can(authUser, CAP.MANAGE_USERS);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white shadow-md p-4 flex justify-between items-center relative z-50">
      <div className="flex flex-col lg:flex-row items-start lg:items-center min-w-0">
        {/* Placeholder for a logo */}
        <Link to="/" className="flex items-center text-gray-800 hover:text-blue-600 transition-colors">
          <img src="/xebia-logo.png" alt="Xebia Logo" className="h-10 mr-1" />
          <h1 className="text-xl font-bold whitespace-nowrap">{BASE_APP_TITLE}</h1>
        </Link>
        {/* Non-prod marker: kept outside the Link so it is not a nav target, and
            driven by the same constants as the browser tab title so they cannot drift. */}
        {isDevEnvironment() && (
          <span
            className="mt-1 lg:mt-0 lg:ml-3 px-2 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-900 text-[10px] sm:text-xs font-semibold"
            title="Development environment — license changes are not sent to Google"
          >
            {DEV_TITLE_SUFFIX}
          </span>
        )}
      </div>
      <div className="flex items-center justify-end">
        <div className="mr-4 sm:mr-6 flex flex-col lg:flex-row items-end lg:items-center">
          <span className="font-semibold text-right text-sm sm:text-base leading-tight truncate max-w-[100px] sm:max-w-[150px] lg:max-w-[200px]" title={authUser.name}>
            {authUser.name}
          </span>
          {canSeeAdminMenu && (
            <span
              className={`mt-1 lg:mt-0 lg:ml-2 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${roleBadgeClass(authUser.role)} whitespace-nowrap`}
              title={`Signed in as ${roleLabel(authUser.role)}`}
            >
              {roleLabel(authUser.role)}
            </span>
          )}
        </div>
        <div className="mr-4 sm:mr-6 relative">
          <Link to="/profile" className="text-gray-600 hover:text-blue-600 relative inline-block" title={t.profile || 'Profile'}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {hasUnseenOrders && (
              <span className="absolute top-0 right-0 inline-flex items-center justify-center p-1 font-bold leading-none text-red-100 bg-red-600 rounded-full w-4 h-4 text-[10px] transform translate-x-1/2 -translate-y-1/2 select-none border border-white">
                !
              </span>
            )}
          </Link>
        </div>
        <div className="mr-4 sm:mr-6">
          <select
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            className="border border-gray-300 rounded-md p-1.5 sm:p-2 text-sm sm:text-base"
          >
            <option value="nl">NL</option>
            <option value="en">EN</option>
          </select>
        </div>
        <div className="flex items-center">
          <button
            onClick={onSummonTour}
            className="bg-purple-100 hover:bg-purple-200 text-primary font-semibold py-1.5 sm:py-2 px-2 sm:px-4 rounded-md transition duration-200 mr-2 sm:mr-4 flex items-center"
            title={t.summon_tour || "Start Tour Guide"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden lg:inline">{t.tour_button || "Tour"}</span>
          </button>
          <button
            onClick={onLogout}
            className="bg-secondary hover:bg-secondary-hover text-white font-bold py-1.5 sm:py-2 px-3 sm:px-4 rounded-md transition duration-200 mr-2 text-sm sm:text-base"
          >
            <span className="hidden sm:inline">{t.logout}</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>

          {canSeeAdminMenu && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={toggleMenu}
                className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
                aria-label="Admin Menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                  <Link
                    to="/workspaces"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    {t.admin_webshop || "Webshops"}
                  </Link>
                  <Link
                    to="/email-templates"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                    </svg>
                    {t.admin_email_template || "Email Templates"}
                  </Link>
                  <Link
                    to="/admin/renewals-summary"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t.admin_renewals_summary || "Renewals Summary"}
                  </Link>
                  <Link
                    to="/admin/renewal-status"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t.admin_renewal_status || "Renewal Status"}
                  </Link>
                  <Link
                    to="/admin/orders-dashboard"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t.admin_orders_dashboard || "Orders Dashboard"}
                  </Link>
                  <Link
                    to="/admin/login-history"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {t.admin_login_history || "Login History"}
                  </Link>
                  <Link
                    to="/admin/afas-transactions"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t.admin_afas_transactions || "AFAS Transactions"}
                  </Link>
                  <Link
                    to="/admin/peppol-invoices"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {t.admin_peppol_invoices || "Peppol Invoices"}
                  </Link>
                  <Link
                    to="/admin/customers"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {t.admin_customers || "Customers"}
                  </Link>
                  <Link
                    to="/admin/flexible-plans"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {t.admin_flexible_plans || "Flexible Plans"}
                  </Link>
                  <Link
                    to="/admin/gcp"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 00-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                    {t.admin_gcp || "GCP"}
                  </Link>
                  <Link
                    to="/admin/jira"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-primary text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    {t.admin_jira || "Jira"}
                  </Link>
                  <Link
                    to="/admin/user-guide"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center border-t border-gray-100 mt-1"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-green-500 text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    {t.admin_user_guide || "User Guide"}
                  </Link>
                  {/* SuperAdmin only, and separated from the rest because it
                      controls access to everything above it. */}
                  {canManageUsers && (
                    <Link
                      to="/admin/users"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center border-t border-gray-100 mt-1"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 bg-purple-600 text-white rounded-full p-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {t.admin_user_management || "User Management"}
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
